/**
 * Shared formatting utilities
 * Centralizes data formatting logic used across components
 */

/**
 * Format salary as abbreviated string (e.g., $120k)
 * @param {number} salary - Salary amount
 * @returns {string}
 */
export const formatSalary = (salary) => {
  if (!salary || salary === 0) return 'N/A';
  return `$${(salary / 1000).toFixed(0)}k`;
};

/**
 * Format salary range
 * @param {Object} job - Job object with salary_range_min and salary_range_max
 * @returns {string}
 */
export const formatSalaryRange = (job) => {
  if (!job) return 'N/A';
  
  const { salary_range_min, salary_range_max } = job;
  
  if (!salary_range_min && !salary_range_max) return 'N/A';
  
  const min = salary_range_min ? formatSalary(salary_range_min) : '';
  const max = salary_range_max ? formatSalary(salary_range_max) : '';
  
  if (min && max) return `${min} - ${max}`;
  return min || max;
};

/**
 * Format years of experience
 * @param {number} years - Years of experience
 * @returns {string}
 */
export const formatExperience = (years) => {
  if (!years || years === 0) return 'N/A';
  return `${years} yrs`;
};

/**
 * Calculate age in days from creation date
 * @param {Object} entity - Entity with date_added or created_at field
 * @returns {number}
 */
export const getAgeInDays = (entity) => {
  if (!entity) return 0;
  
  // Use actual add date from CSV, not migration timestamp
  const date = entity.date_added || entity.created_at;
  if (!date) return 0;
  
  const createdDate = new Date(date);
  const now = new Date();
  return Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
};

/**
 * Format date added with relative time
 * @param {Object} entity - Entity with date_added or created_at field
 * @returns {string}
 */
export const formatDateAdded = (entity) => {
  if (!entity) return 'N/A';
  
  // CRITICAL: Use actual add date from CSV (date_added), NOT migration timestamp (created_at)
  const date = entity.date_added || entity.created_at;
  if (!date) return 'N/A';
  
  const addedDate = new Date(date);
  const now = new Date();
  const diffTime = Math.abs(now - addedDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Format based on recency
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return `${diffDays}d ago`;
  if (diffDays <= 30) return `${Math.floor(diffDays / 7)}w ago`;
  
  // Show actual date if older than 30 days
  return addedDate.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

/**
 * Check if entity was recently added (within 7 days)
 * @param {Object} entity - Entity with date_added or created_at field
 * @returns {boolean}
 */
export const isRecentlyAdded = (entity) => {
  if (!entity) return false;
  
  // CRITICAL: Use actual add date from CSV (date_added), NOT migration timestamp (created_at)
  const date = entity.date_added || entity.created_at;
  if (!date) return false;
  
  const addedDate = new Date(date);
  const now = new Date();
  const diffTime = Math.abs(now - addedDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays <= 7;
};

/**
 * Get color class for age display
 * @param {number} daysOld - Age in days
 * @returns {string}
 */
export const getAgeColorClass = (daysOld) => {
  if (daysOld <= 7) return 'text-green-600';
  if (daysOld <= 30) return 'text-yellow-600';
  if (daysOld <= 60) return 'text-orange-600';
  if (daysOld <= 90) return 'text-red-600';
  return 'text-red-800 font-bold';
};

/**
 * Get top skills from candidate
 * @param {Object} candidate - Candidate object
 * @param {number} limit - Number of skills to return
 * @returns {string}
 */
export const getTopSkills = (candidate, limit = 3) => {
  if (!candidate) return 'N/A';
  
  const skills = candidate.skills || candidate.technical_skills || [];
  if (skills.length === 0) return 'N/A';
  
  return skills.slice(0, limit).join(', ');
};

/**
 * Get priority badge style
 * @param {string} priority - Priority level (high, medium, low)
 * @returns {Object}
 */
export const getPriorityBadgeStyle = (priority) => {
  const priorityLower = (priority || 'low').toLowerCase();
  
  if (priorityLower === 'high' || priorityLower === 'urgent') {
    return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' };
  }
  
  if (priorityLower === 'medium' || priorityLower === 'normal') {
    return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' };
  }
  
  return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
};
